import * as React from 'react';
import { useDispatch } from 'react-redux';
import {
  type IRequestAuthenticationParameters,
  openAuthenticationModal,
  requestAuthentication,
} from 'shared/actions/authentication';
import { changeFavorite } from 'shared/actions/favorite';
import { openNewbuildingBrokerCallbackPopup } from 'shared/actions/newbuildingBrokerCallbackWidget';
import { IQsToUris } from 'shared/api/offers';
import {
  ICoordinatesOffers,
  requestSuggestionsDistances,
  setQsToUris,
  setSuggestionDistancesSeoText,
} from 'shared/serp/state/suggestions';

import { ICallbackHookParams, IOffersCallbacks } from './types';
import { openChat, openChatAfterAuth } from '../../actions/chat';
import { openNotAvailablePopup } from '../../actions/notAvailablePopup';
import { type TAgentRating } from '../../api/agent';
import { Dispatch } from '../../common/state';
import { type IOffer, type TOfferCategory } from '../../offer';
import { hideOfferAction, hideOfferAfterAuthAction, type IHidingOfferInfo } from '../../serp/state/hide_offer';
import { hideMotivationPopup } from '../../serp/state/login_motivation_popup';
import { updateAgentRating } from '../../serp/state/offer_card/agent';
import { changeOfferComments } from '../../serp/state/offer_card/comments';
import { addOfferComplaint } from '../../serp/state/offer_card/complaints';
import {
  type EFeedbackComplaint,
  type IComplaintFeedbackBody,
  sendFeedbackComplaint,
  statusChanger,
} from '../../serp/state/offer_card/feedback_complaint';
import {
  simplifiedOfferCardPopupCloseAction,
  simplifiedOfferCardPopupOpenAction,
} from '../../serp/state/offer_card/simplified_card_popups';
import {
  addOfferToComparison,
  addOfferToComparisonAfterAuth,
  deleteOfferFromComparison,
} from '../../serp/state/offersComparison';

export const useOffersCallbacks = ({ userIsAuthenticated }: ICallbackHookParams): IOffersCallbacks => {
  const dispatch = useDispatch<Dispatch>();

  const closePopup = React.useCallback(() => dispatch(hideMotivationPopup()), [dispatch]);
  const onAgentRatingChanged = React.useCallback(
    (offer: IOffer, rating: TAgentRating) => dispatch(updateAgentRating(offer, rating)),
    [dispatch],
  );
  const onComplaintSent = React.useCallback(
    (offerId: number, name: string, message?: string) => dispatch(addOfferComplaint(offerId, name, message)),
    [dispatch],
  );
  const onFavoriteChanged = React.useCallback(
    (offer: IOffer, isFavorite: boolean) => dispatch(changeFavorite(offer, isFavorite)),
    [dispatch],
  );
  const onOfferCommentsChanged = React.useCallback(
    (offer: IOffer, commentOffer: string | undefined, commentAgent: string | undefined) =>
      dispatch(changeOfferComments(offer, commentOffer, commentAgent)),
    [dispatch],
  );
  const onPopupMoreOpen = React.useCallback(
    (offerId: number) => dispatch(simplifiedOfferCardPopupOpenAction(offerId, 'more')),
    [dispatch],
  );
  const onPopupReportOpen = React.useCallback(
    (offerId: number) => dispatch(simplifiedOfferCardPopupOpenAction(offerId, 'report')),
    [dispatch],
  );
  const onPopupMoreClose = React.useCallback(
    (offerId: number) => dispatch(simplifiedOfferCardPopupCloseAction(offerId, 'more')),
    [dispatch],
  );
  const onPopupReportClose = React.useCallback(
    (offerId: number) => dispatch(simplifiedOfferCardPopupCloseAction(offerId, 'report')),
    [dispatch],
  );
  const onUserInfoPopupOpen = React.useCallback(
    (offerId: number) => dispatch(simplifiedOfferCardPopupOpenAction(offerId, 'agent')),
    [dispatch],
  );
  const onUserInfoPopupClose = React.useCallback(
    (offerId: number) => dispatch(simplifiedOfferCardPopupCloseAction(offerId, 'agent')),
    [dispatch],
  );
  const hideOfferActionCallback = React.useCallback(
    (hidingOffer: IHidingOfferInfo) => dispatch(hideOfferAction(hidingOffer)),
    [dispatch],
  );
  const hideOfferAfterAuthActionCallback = React.useCallback(
    (hidingOffer: IHidingOfferInfo) => dispatch(hideOfferAfterAuthAction(hidingOffer)),
    [dispatch],
  );
  // hideOfferAfterAuthAction: (hidingOffer: IHidingOfferInfo) => {
  //         dispatch(hideOfferAction(hidingOffer));
  //       },
  const sendComplaintFeedbackCallback = React.useCallback(
    (body: IComplaintFeedbackBody) => dispatch(sendFeedbackComplaint(body)),
    [dispatch],
  );
  const statusChangerCallback = React.useCallback(
    (status: EFeedbackComplaint) => dispatch(statusChanger(status)),
    [dispatch],
  );
  const addToComparison = React.useCallback(
    ({ offerId, category }: { offerId: number; category: TOfferCategory | null }) =>
      dispatch(addOfferToComparison({ offerId, category })),
    [dispatch],
  );
  const addToComparisonAfterAuth = React.useCallback(
    ({ offerId, category }: { offerId: number; category: TOfferCategory | null }) =>
      dispatch(addOfferToComparisonAfterAuth({ offerId, category })),
    [dispatch],
  );
  const deleteFromComparison = React.useCallback(
    ({ offerId }: { offerId: number }) => dispatch(deleteOfferFromComparison({ offerId })),
    [dispatch],
  );
  const openChatHandler = React.useCallback(
    (offer: IOffer, position: number, isEarlyAccess?: boolean, placeType?: string) => {
      if (!userIsAuthenticated) {
        dispatch(openChatAfterAuth({ offer, position, placeType }));
        dispatch(openAuthenticationModal());

        return;
      }

      dispatch(openChat({ offer, position, isEarlyAccess, placeType }));
    },
    [dispatch, userIsAuthenticated],
  );

  const openNotAvailablePopupHandler = React.useCallback(
    (offer: IOffer, position: number) => dispatch(openNotAvailablePopup({ offer, position })),
    [dispatch],
  );
  const openAuthenticationPopupHandler = React.useCallback(
    (parameters?: IRequestAuthenticationParameters) => dispatch(requestAuthentication(parameters)),
    [dispatch],
  );
  const openNewbuildingBrokerCallbackPopupHandler = React.useCallback(
    (offer: IOffer, position: number) => dispatch(openNewbuildingBrokerCallbackPopup(offer, position)),
    [dispatch],
  );
  const requestSuggestionsDistancesCallback = React.useCallback(
    (coordinatesOffers?: ICoordinatesOffers[]) => dispatch(requestSuggestionsDistances(coordinatesOffers)),
    [dispatch],
  );
  const setQsToUrisCallback = React.useCallback((qsToUris: IQsToUris) => dispatch(setQsToUris(qsToUris)), [dispatch]);
  const setSuggestionDistancesSeoTextCallback = React.useCallback(
    (text: string) => dispatch(setSuggestionDistancesSeoText(text)),
    [dispatch],
  );

  return React.useMemo(
    () => ({
      closePopup,
      onAgentRatingChanged,
      onComplaintSent,
      onFavoriteChanged,
      onOfferCommentsChanged,
      onPopupMoreOpen,
      onPopupReportOpen,
      onPopupMoreClose,
      onPopupReportClose,
      onUserInfoPopupOpen,
      onUserInfoPopupClose,
      hideOfferAction: hideOfferActionCallback,
      hideOfferAfterAuthAction: hideOfferAfterAuthActionCallback,
      sendComplaintFeedback: sendComplaintFeedbackCallback,
      statusChanger: statusChangerCallback,
      addToComparison,
      addToComparisonAfterAuth,
      deleteFromComparison,
      openChat: openChatHandler,
      openNotAvailablePopup: openNotAvailablePopupHandler,
      openAuthenticationPopup: openAuthenticationPopupHandler,
      openNewbuildingBrokerCallbackPopup: openNewbuildingBrokerCallbackPopupHandler,
      requestSuggestionsDistances: requestSuggestionsDistancesCallback,
      setQsToUris: setQsToUrisCallback,
      setSuggestionDistancesSeoText: setSuggestionDistancesSeoTextCallback,
    }),
    [
      closePopup,
      onAgentRatingChanged,
      onComplaintSent,
      onFavoriteChanged,
      onOfferCommentsChanged,
      onPopupMoreOpen,
      onPopupReportOpen,
      onPopupMoreClose,
      onPopupReportClose,
      onUserInfoPopupOpen,
      onUserInfoPopupClose,
      hideOfferActionCallback,
      hideOfferAfterAuthActionCallback,
      sendComplaintFeedbackCallback,
      statusChangerCallback,
      addToComparison,
      addToComparisonAfterAuth,
      deleteFromComparison,
      openChatHandler,
      openNotAvailablePopupHandler,
      openAuthenticationPopupHandler,
      openNewbuildingBrokerCallbackPopupHandler,
      requestSuggestionsDistancesCallback,
      setQsToUrisCallback,
      setSuggestionDistancesSeoTextCallback,
    ],
  );
};
